import React, {useState, useEffect, useCallback, memo } from 'react'
import { Box, Typography, Button, Checkbox, MenuItem, ListItemText, Snackbar, Popover, Tabs, Tab, Stack, CircularProgress } from '@mui/material';
import MuiAlert from '@mui/material/Alert';
import { useNavigate, useLocation } from 'react-router-dom';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import IconButton from '@mui/material/IconButton';
import EventIcon from '@mui/icons-material/Event';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { StaticDatePicker } from '@mui/x-date-pickers/StaticDatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import jaLocale from 'date-fns/locale/ja';
import { styled, useTheme } from '@mui/system';
import { DataGrid, GridRow, useGridApiRef } from '@mui/x-data-grid';
import { jaJP } from '@mui/x-data-grid/locales';
import dayjs from 'dayjs';
import { apiURL, userURL } from './App';
import { authJson, authFetch } from './apiClient';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
// dnd-kit
import { useSortable } from '@dnd-kit/sortable';
import { useDraggable } from '@dnd-kit/core';
import { useDroppable } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';

import {
  DndContext,
  DragOverlay,
  pointerWithin,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent
} from "@dnd-kit/core";
import {
  restrictToVerticalAxis,
  restrictToWindowEdges,
  restrictToParentElement
} from "@dnd-kit/modifiers";
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy
} from "@dnd-kit/sortable";

// 共通コンテナスタイル
const Container = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  //padding: '30px',
  backgroundColor: '#fff',
  position: 'relative',
  width: '100%',
  height: '100%'
});

// DataGrid カスタムスタイル
export const ThemeDataGrid = styled(DataGrid)(({ theme }) => ({
  backgroundColor: theme.palette.secondary.light,
  '& .MuiDataGrid-columnHeader': {
    backgroundColor: theme.palette.secondary.light,
    color: theme.palette.primary.main,
  },
  // ヘッダクリック時のフォーカス枠を消す
  '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within, & .MuiDataGrid-columnHeader:focus-visible': {
    outline: 'none',
  },
  '& .MuiDataGrid-menuIconButton, & .MuiDataGrid-menuIcon, & .MuiDataGrid-iconButtonContainer': {
    display: 'none !important',
  },
  '& .MuiDataGrid-filler': { backgroundColor: theme.palette.secondary.light },
  '& .MuiDataGrid-root': { backgroundColor: theme.palette.secondary.light },
  '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': { outline: 'none' },
}));

// DataGrid カスタムスタイル（グレー背景）
export const GrayDataGrid = styled(DataGrid)(({ theme }) => ({
  backgroundColor: '#f5f5f5',
  '& .MuiDataGrid-columnHeader': {
    backgroundColor: '#f5f5f5',
    color: theme.palette.primary.main,
  },
  // ヘッダクリック時のフォーカス枠を消す
  '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within, & .MuiDataGrid-columnHeader:focus-visible': {
    outline: 'none',
  },
  '& .MuiDataGrid-menuIconButton, & .MuiDataGrid-menuIcon, & .MuiDataGrid-iconButtonContainer': {
    display: 'none !important',
  },
  '& .MuiDataGrid-filler': { backgroundColor: '#f5f5f5' },
  '& .MuiDataGrid-root': { backgroundColor: '#f5f5f5' },
  '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': { outline: 'none' },
  // 行ホバー時のみ表示するアクションボタン
  '& .row-action': {
    visibility: 'hidden',
  },
  '& .MuiDataGrid-row:hover .row-action': {
    visibility: 'visible',
  },
  // 行が編集モードの間は常に非表示（ホバー時も）
  '& .MuiDataGrid-row--editing .row-action': {
    visibility: 'hidden',
  },
  '& .MuiDataGrid-row--editing:hover .row-action': {
    visibility: 'hidden',
  },
}));

// ドラッグ可能な行コンポーネント
// MUI v7: GridRowProps 内の id を利用 (rowId が無いケース対策で 両方見る)
const DraggableGridRow = memo((params) => {
  const { rowId, index, activeId, totalRows } = params; 
  const [isBottomHalf, setIsBottomHalf] = useState(false);

  //console.log(`Rendering row ${rowId} at index ${index}, activeId: ${activeId}, isBottomHalf: ${isBottomHalf}`);
  // ドロップ先(over)の情報（indexとisBottomHalf）を useSortable の data に入れておく
  const { 
    attributes, listeners, setNodeRef, transform, transition, isDragging, isOver 
  } = useSortable({ 
    id: rowId,
    data: {
      index: index,
      isBottomHalf: isBottomHalf
    }
  }); 

  // マウス位置が行の上半分か下半分が判定
  const handleMouseMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const offsetY = e.clientY - rect.top;
    const currentIsBottom = offsetY > rect.height / 2;  
    // Stateが実際に変わる時だけ更新（無駄な再レンダリング防止）
    if (currentIsBottom !== isBottomHalf) {
      setIsBottomHalf(currentIsBottom);
    }
  };

  // 外部からのドラッグ中のみ青線を出す判定
  const isExternalDragging = activeId?.toString().startsWith('external-');
  
  // 重要：isOver だけでなく、自分がドラッグ対象（isDragging）でないことも条件に入れる
  const showLine = isExternalDragging && isOver && !isDragging;
  const isLastRow = index === totalRows - 1;
  const showTopLine = showLine && (!isLastRow || !isBottomHalf);
  const showBottomLine = showLine && (isLastRow && isBottomHalf);
  
  const style = {
    transform: transform ? CSS.Transform.toString(transform) : undefined,
    transition,
    opacity: isDragging ? 0.3 : 1,
    position: 'relative',
    zIndex: isOver ? 100 : 1, // 乗っている時だけ少し浮かせる

    // 青線：showLine が true の時だけ出す
    borderTop: showTopLine ? '3px solid #1976d2' : 'none',
    borderBottom: showBottomLine ? '3px solid #1976d2' : 'none',
  };

  return (
    <div 
      ref={setNodeRef} 
      style={style}
      onMouseMove={handleMouseMove}
      {...attributes} 
      {...listeners}
    >
      <GridRow {...params} />
    </div>
  );
});

// ドラッグ可能なDataGrid
const DraggableDataGrid = memo(({ columns, rows, activeId }) => {
  const { setNodeRef, isOver } = useDroppable({
    id: 'draggable-datagrid-droppable', 
  });

  const isExternalDragging = activeId?.toString().startsWith('external-');
  const showEmptyGuide = isExternalDragging && rows.length === 0 && isOver;

  return (
    <Box 
      id="draggable-datagrid-container" 
      ref={setNodeRef} 
      sx={{ 
        flex: 1, 
        display: 'flex', 
        width: '100%', 
        minHeight: '300px', // 少し高めに確保しておくと安心
        position: 'relative',
        flexDirection: 'column'
      }}
    >
      {/* 1. 線ガイド：ヘッダー(56px)のすぐ下に配置 */}
      {showEmptyGuide && (
        <Box sx={{
          position: 'absolute',
          top: 56, 
          left: 0,
          right: 0,
          borderTop: '3px solid #1976d2',
          zIndex: 10,
          pointerEvents: 'none'
        }} />
      )}

      <SortableContext
        items={rows.map((row) => row.id)}
        strategy={verticalListSortingStrategy}
      >
        <GrayDataGrid
          rows={rows}
          columns={columns}
          localeText={jaJP.components.MuiDataGrid.defaultProps.localeText}
          hideFooter
          slots={{ row: DraggableGridRow }}
          slotProps={{
            row: {
              activeId: activeId,
              totalRows: rows.length,
            },
          }}
          // ...略...
          sx={{
            flex: 1,
            height: '100%',
            // 「データがありません」の文字を少し下にずらすか、
            // あるいは自前のガイドを表示している間は隠す設定
            '& .MuiDataGrid-overlay': {
              top: showEmptyGuide ? '100px' : '0', // ガイドとかぶらないように逃がす
            }
          }}
        />
      </SortableContext>

      {/* 2. テキストガイド：線のすぐ下に配置 */}
      {rows.length === 0 && (
        <Box sx={{ 
          position: 'absolute', 
          top: 100, // 線の少し下
          left: '50%', 
          transform: 'translateX(-50%)', 
          color: '#999', 
          pointerEvents: 'none',
          zIndex: 10
        }}>
          ここにドロップして追加
        </Box>
      )}
    </Box>
  );
});

// 外部DataGridからのドラッグ用コンポーネント
const ExternalDraggableCell = ({ id, row, type, children }) => {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    // IDが左のリストと重複しないようプレフィックスを付ける
    id: `external-${type}-${id}`,
    data: { type, row }, 
  });

  const style = {
    opacity: isDragging ? 0.5 : 1,
    cursor: 'grab',
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    height: '100%',
  };

  return (
    <Box ref={setNodeRef} style={style} {...listeners} {...attributes}>
      {children}
    </Box>
  );
};

// ドロップ元DataGrid用コンテナ
const LeftDroppableContainer = ({ children }) => {
  const { setNodeRef } = useDroppable({
    id: 'scrollable-container', // これが event.over.id になります
  });

  return (
    <Box
      ref={setNodeRef} // これが重要！
      id="scrollable-container"
      sx={{ 
        flex: 1, 
        display: 'flex', 
        flexDirection: 'column', 
        minHeight: 0, 
        overflowY: 'auto' 
      }}
    >
      {children}
    </Box>
  );
};

// 放送編集ページ
const BroadcastEditPage = (props) => {
  const navigate = useNavigate();
  const routerLocation = useLocation();
  const theme = useTheme();
  const apiRef = useGridApiRef();
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [calendarAnchor, setCalendarAnchor] = useState(null);
  const [users, setUsers] = useState([]);
  const [broadcastRows, setBroadcastRows] = useState([]);
  const [interviewRows, setInterviewRows] = useState([]);
  const [clipRows, setClipRows] = useState([]);
  const [date, setDate] = useState(null);

  // 担当者セル編集制御: シンプル化のため強制オープンは行わない
  // 担当者Popover開閉 (行ID管理) - 選択更新で再レンダーしても開いたまま維持するため親で状態持つ
  const [personMenuOpenRowId, setPersonMenuOpenRowId] = useState(null);
  const [personMenuPosition, setPersonMenuPosition] = useState(null); // { top, left }
  const personMenuItemRefs = React.useRef([]); // roving focus refs
  const skipNextClickRef = React.useRef(false); // spaceでの擬似click抑止
  // 担当者Popover: オリジナル行スナップショット & コミット管理
  const [personMenuOriginalRow, setPersonMenuOriginalRow] = useState(null); // {id, ...oldRow}

  // トースト（showToastを下位useCallbackで参照するため上に配置）
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMsg, setToastMsg] = useState('');
  const [toastSeverity, setToastSeverity] = useState('success');
  const showToast = useCallback((msg, severity='success') => {
    setToastMsg(msg); setToastSeverity(severity); setToastOpen(true);
  }, []);

  // DataGridドラッグ用
  const [activeId, setActiveId] = useState(null);
  const [cursorMode, setCursorMode] = useState('grabbing');

  // ハンドル(先頭セル)のみドラッグ可能にするカスタムセンサー
  class HandlePointerSensor extends PointerSensor {
    static activators = [
      {
        eventName: 'onPointerDown',
        handler: ({ nativeEvent }) => {
          const handle = nativeEvent.target && nativeEvent.target.closest && nativeEvent.target.closest('[data-drag-handle="true"]');
          return !!handle; // ハンドル要素を起点にした場合のみ有効化
        }
      }
    ];
  }
  // センサー登録
  const sensors = useSensors(useSensor(HandlePointerSensor));

  // DataGrid 列定義 (放送予定)
  const broadcastColumns = [
    { field:'onair_order', headerName:'OA順番', width:80, sortable:false, renderCell:(params)=> (
      <Box 
        data-drag-handle="true" 
        sx={{ display:'flex', alignItems:'center', gap:0.5, cursor:'grab', width:'100%', height:'100%' }}>
        <DragIndicatorIcon fontSize="small" sx={{ color:'#666' }} />
        <span style={{ lineHeight:1 }}>{params.value}</span>
      </Box>
    ) },
    {
      field: 'program_name',
      headerName: '名称',
      width: 200,
      sortable: false,
      renderCell: (params) => {
        const row = params.row;
        if (row.interview_id != null) {
          return row.interview_name;
        } else if (row.clip_id != null) {
          return row.clip_name;
        }
        return '';
      }
    },
    {
      field: 'program_length',
      headerName: '放送尺',
      width: 100,
      sortable: false,
      renderCell: (params) => {
        const row = params.row;
        if (row.interview_id != null) {
          return row.program_length;
        } else if (row.clip_id != null) {
          return row.clip_length;
        }
        return '';
      }
    },
    {
      field: 'program_ontents',
      headerName: '内容',
      width: 200,
      sortable: false,
      renderCell: (params) => {
        const row = params.row;
        if (row.interview_id != null) {
          return row.interview_contents;
        } else if (row.clip_id != null) {
          return '';
        }
        return '';
      }
    },
    {
      field: 'actions',
      headerName: '',
      width: 80,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <div style={{display: 'flex', gap: 4, height: '100%', alignItems: 'center', justifyContent: 'center'}}>
          <IconButton
            className="row-action"
            size="small"
            color="error"
            aria-label="削除"
            onClick={(e) => { e.stopPropagation(); handleDeleteRow(params.row); }}
          >
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </div>
      )
    }    
  ];

  // DataGrid 列定義 (取材予定)
  const interviewColumns = [
    {
      field: "drag",
      headerName: "",
      width: 50,
      sortable: false,
      renderCell: (params) => (
        <ExternalDraggableCell id={params.id} row={params.row} type="interview">
          <Box
            data-drag-handle="true"
            sx={{
              display: "flex",
              alignItems: "center",
              cursor: "grab",
              width: "100%",
              height: "100%"
            }}
          >
            <DragIndicatorIcon fontSize="small" sx={{ color: "#666" }} />
          </Box>
        </ExternalDraggableCell>
      )
    },
    { field:'interview_name', headerName:'取材名', width:150, sortable:false },
    { field:'interview_place', headerName:'取材場所', width:150, sortable:false },
    { field:'ucv_person_ids', headerName:'担当者', width:130, sortable:false,
      renderCell:(params)=> <PersonsCell row={params.row} /> },
    { field:'is_onair', headerName:'放送', width:80, sortable:false, filterable:false, align:'center', headerAlign:'center',
      renderCell:(params)=>{
        const checked = params.row.mark === 1 && params.row.is_onair;
        const disabled = params.row.mark !== 1; // mark=1 (〇) の時のみ有効
        return (
          <Checkbox
            size="small"
            checked={checked}
            disabled={disabled}
            onClick={(e)=>e.stopPropagation()}
            onChange={(e)=>{
              e.stopPropagation();
              if(disabled) return; // 念のため防御
              toggleOnair(params.row.id);
            }}
          />
        );
      }
    },
    // 放送日列を一番右に追加
    { field:'program_date', headerName:'放送日', width:100, sortable:false, align:'center', headerAlign:'center', resizable:false,
      renderCell:(params)=> <ProgramDateCell row={params.row} /> },
  ];

  // 放送日セル: 日付+カレンダーアイコン+カレンダーPopover
  const ProgramDateCell = ({ row }) => {
    const [open, setOpen] = React.useState(false);
    const [anchorEl, setAnchorEl] = React.useState(null);
    const [value, setValue] = React.useState(row.program_date ? dayjs(row.program_date) : null);

    // ProgramDateCell の関数内
    if (!window.__suppressRowClickRef) window.__suppressRowClickRef = { current: false };
    const suppressRowClickRef = window.__suppressRowClickRef;

    // アイコンクリックでPopover開く
    const handleIconClick = (event) => {
      // クリックイベントが行クリックに伝播して詳細画面に遷移するのを防止
      event.stopPropagation();
      // 現在の program_date を日付オブジェクトに変換してセット（null/invalid なら null）
      const d = row.program_date ? dayjs(row.program_date) : null;
      setValue(d && d.isValid() ? d.toDate() : null);
      // Popoverのアンカーセットとオープン
      setAnchorEl(event.currentTarget);
      setOpen(true);
    };

    // 取材リスト更新 (取材日変更コミット)
    // 変更があればDB更新し、成功したらrows更新 & 編集モード停止
    // 失敗したら旧行を返す
    async function programDateRowUpdate(newRow, oldRow) {
      // 変更があればDB更新
      const changed = oldRow?.program_date !== newRow?.program_date;
      if(!changed) return newRow; // 変更なし
      // 未放送リストから放送予定リストへ追加
      await addInterviewToBroadcast(newRow, -1);
    }

    // 日付変更時の処理: カレンダーで日付選択 → DB更新 → 取材リスト・放送リスト再読み込み
    const handleDateChange = async (newValue) => {
      setValue(newValue);
      setOpen(false);
      let formatted = '';
      if (newValue) {
        const d = (typeof newValue.format === 'function') ? newValue : dayjs(newValue);
        formatted = d.isValid() ? d.format('YYYY-MM-DD') : '';
      }
      // DB更新と取材リスト更新を行う関数に委譲（成功したら取材リストも更新、失敗したらトースト表示）
      await commitProgramDateChanges(row.id, formatted);
      // 取材リスト・放送リスト再読み込み
      if (typeof fetchBroadcastRows === 'function') fetchBroadcastRows();
      if (typeof fetchInterviewRows === 'function') fetchInterviewRows();
    };

    // 放送日Popover 閉鎖時コミット
    const commitProgramDateChanges = useCallback((rowId, date)=> {
      const current = interviewRows.find(r => r.id === rowId);
      if(current){
        // 変更があればDB更新
        programDateRowUpdate({ ...current, program_date: date }, current);
      }
    }, [interviewRows]);
    
    // program_dateがnull, undefined, 空文字の場合は「未設定」と表示
    const displayDate = (!row.program_date || row.program_date === 'NULL') ? '未設定' : row.program_date;
    return (
      <Box sx={{ display:'flex', alignItems:'center', justifyContent:'center', width:'100%', height:'100%' }}>
        <Typography variant="body2" fontSize="inherit" sx={{ minWidth:0, flex:1, textAlign:'center', display:'flex', alignItems:'center', height:'100%' }}>
          {displayDate}
        </Typography>
        <IconButton 
          size="small"
          color="primary"
          onMouseDown={() => { suppressRowClickRef.current = true; }}
          onClick={handleIconClick} 
          sx={{ ml:0.5, display:'flex', alignItems:'center', height:'100%' }}>
          <EventIcon fontSize="small"/>
        </IconButton>
        <Popover
          open={open}
          anchorEl={anchorEl}
          onMouseDown={() => { suppressRowClickRef.current = true; }}
          onClose={() => setOpen(false)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          transformOrigin={{ vertical: 'top', horizontal: 'center' }}
          slotProps={{ paper: { sx: { p: 1 } } }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mb: 1 }}>
            <Button size="small" onClick={() => { setOpen(false); }}>閉じる</Button>
          </Box>
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={jaLocale}>
            <StaticDatePicker
              displayStaticWrapperAs="desktop"
              value={value}
              onChange={handleDateChange}
            />
          </LocalizationProvider>
        </Popover>
      </Box>
    );
  };
  
  // 担当者セル: 常時表示のアイコンでPopover開閉
  const PersonsCell = ({ row }) => {
    const ids = Array.isArray(row.ucv_person_ids) ? row.ucv_person_ids : [];
    const names = ids.map(id => users.find(u => u.id === id)?.name).filter(Boolean).join(', ');
    const containerRef = React.useRef(null);
    return (
      <Box ref={containerRef} sx={{display:'flex', alignItems:'center', width:'100%', gap:0.5, minHeight:'100%', position:'relative'}}>
        <Box
          component="span"
          sx={{whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis', flex:1, cursor:'pointer'}}
          title={names}
        >
          {names || ''}
        </Box>
        <IconButton
          size="small"
          onClick={(e)=>{ 
            e.stopPropagation();
            if(personMenuOpenRowId === row.id){
              // 閉じる: 変更があればコミット
              commitPersonMenuChanges();
              setPersonMenuPosition(null);
              setTimeout(()=> setPersonMenuOpenRowId(null), 30);
              return;
            }
            // 他行が開いていたら先にコミット
            if(personMenuOpenRowId && personMenuOpenRowId !== row.id){
              commitPersonMenuChanges();
            }
            // 新規オープン: オリジナルスナップショット保存
            setPersonMenuOriginalRow({ id: row.id, ucv_person_ids: Array.isArray(row.ucv_person_ids)? [...row.ucv_person_ids]: [] });
            const el = containerRef.current || e.currentTarget;
            if(el){
              const rect = el.getBoundingClientRect();
              setPersonMenuPosition({ top: rect.bottom + window.scrollY, left: rect.left + window.scrollX });
            }
            setPersonMenuOpenRowId(row.id);
          }}
          sx={{ p:0.25 }}
          aria-label="担当者編集"
        >
          <ArrowDropDownIcon fontSize="small" />
        </IconButton>
      </Box>
    );
  };

  // DataGrid 列定義 (固定クリップ)
  const clipColumns = [
    {
      field: "drag",
      headerName: "",
      width: 50,
      sortable: false,
      renderCell: (params) => (
        <ExternalDraggableCell id={params.id} row={params.row} type="clip">
          <Box
            data-drag-handle="true"
            sx={{
              display: "flex",
              alignItems: "center",
              cursor: "grab",
              width: "100%",
              height: "100%"
            }}
          >
            <DragIndicatorIcon fontSize="small" sx={{ color: "#666" }} />
          </Box>
        </ExternalDraggableCell>
      )
    },
    { field: "name", headerName: "クリップ名", width: 200 },
    { field: "length", headerName: "クリップ尺", width: 100 }
  ];

  // 初期表示
  useEffect(() => {
    // セッションストレージから保存日付を復元 (ISO形式で保存している前提)
    const isoStr = sessionStorage.getItem('broadcast_date');
    if (isoStr) {
      setDate(new Date(isoStr));
    } else {
      setDate(new Date());
    }    
    // ユーザ取得
    (async () => {
      try {
        const data = await authJson(`${userURL}/fullnames/`);
        setUsers(Array.isArray(data) ? data : []);
      } catch(e){ setUsers([]); }
    })();
    // 取材リスト取得
    fetchClipRows();
    // 固定クリップリスト取得
    fetchInterviewRows();
  }, []);

  // 日付変更時: 取材リストのみ先に取得（スケジュールはユーザ取得後の別effectで）
  useEffect(() => {
    if (date) {
      // 日付をセッションストレージに保存（ISO形式）
      sessionStorage.setItem('broadcast_date', date.toISOString());
      // 放送予定リスト取得
      fetchBroadcastRows();
    }
  }, [date]);

  // 取材リスト変更時: 放送リスト更新
  useEffect(() => {
    // 放送予定リスト取得
    fetchBroadcastRows();
  }, [interviewRows]);

  // 放送リスト変更時
  useEffect(() => {
    // 放送リスト再採番共通処理 (force=true)
    resequenceBroadcast(broadcastRows, { force: true });
  }, [broadcastRows]);

  // 放送予定リスト取得
  const fetchBroadcastRows = async () => {
    if (!date) return;
    let newRows = [];
    const selectStr = date.toLocaleDateString('ja-JP');
    const query_params = new URLSearchParams({  program_date: selectStr });
    try {
      const data = await authJson(`${apiURL}/broadcasts?` + query_params);
      newRows = data;
    } catch(e){ console.log(e); }
    setBroadcastRows(newRows);
  };

  // 取材リスト取得(未放送の全取材予定)
  const fetchInterviewRows = async () => {
    let newRows = [];
    // 検索条件：放送ありかつ放送日未設定
    const condition = { is_onair: true, program_date: null };
    // フォームデータ追加
    const body = new FormData()
    body.append("props", JSON.stringify(condition))
    console.log(...body.entries());
    try {
      const data = await authJson(`${apiURL}/interviews`, { method: 'POST', body: body, actionName: 'GetInterviews' });
      newRows = data;
    } catch(e){ console.log(e); }
    setInterviewRows(newRows);
  };
  
  // 固定クリップリスト取得
  const fetchClipRows = async () => {
    let newRows = [];
    try {
      console.log(`${apiURL}/master/clips`);
      const data = await authJson(`${apiURL}/master/clips`);
      newRows = data;
    } catch(e){ console.log(e); }
    // DataGridのドラッグハンドル列用にdragフィールドを追加
    setClipRows(newRows.map(row => ({ ...row, drag: true })));
  };

  // 放送尺合計計算関数
  function sumProgramLength(rows) {
    // program_length, clip_length どちらも考慮
    let totalSeconds = 0;
    for (const row of rows) {
      let len = null;
      if (row.interview_id != null) {
        len = row.program_length;
      } else if (row.clip_id != null) {
        len = row.clip_length;
      }
      if (typeof len === 'string' && len.match(/^\d{1,2}:\d{2}:\d{2}$/)) {
        // HH:MM:SS 形式
        const [h, m, s] = len.split(":").map(Number);
        totalSeconds += h * 3600 + m * 60 + s;
      } else if (typeof len === 'number') {
        // 秒数で渡ってきた場合
        totalSeconds += len;
      }
    }
    // HH:MM:SS 形式で返す
    const h = Math.floor(totalSeconds / 3600);
    const m = Math.floor((totalSeconds % 3600) / 60);
    const s = totalSeconds % 60;
    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  }
  
  // 放送リスト再採番共通処理
  // 用途: 1) D&D 並び替え完了時 (id 並びが変わった時のみ保存) 2) 放送タブ表示時 (force=true: 欠番/未設定/ズレを補正して連番化)
  // 引数 options.force: true の場合は id 並びが同じでも onair_order が理想連番でないなら保存
  const resequenceBroadcast = useCallback((list, { force=false } = {}) => {
    console.log('放送リスト再採番開始', { list, force });
    if (!Array.isArray(list) || !list.length) return;
    // 1..n を割り当てた理想状態
    const resequenced = list.map((r, idx) => ({ ...r, onair_order: idx + 1 }));
    const beforeIds = broadcastRows.map(r => r.id).join(','); // 現在 state の表示順 (id 並び)
    const afterIds = resequenced.map(r => r.id).join(',');    // 引数 list の表示順 (id 並び)
    // 並び差分判定 (ドラッグ時用)
    const orderChanged = beforeIds !== afterIds;
    // 連番ズレ/欠番/未設定判定 (force 用)
    const numberingChanged = resequenced.some((r, i) => r.onair_order !== (broadcastRows[i] ? broadcastRows[i].onair_order : undefined));
    // 更新要否
    if (!force) {
      // 通常(D&D)は id 並びが変わっていなければスキップ
      if (!orderChanged) return;
    } else {
      // force モード: 並び変更なし & 番号ズレなしなら何もしない
      if (!orderChanged && !numberingChanged) return;
    }
    console.log('放送リスト再採更新', { resequenced });
    // 放送リストバックアップ (失敗時ロールバック)
    const backup = broadcastRows;
    // 放送リストUI先行更新 ※これをしないとドラッグ完了後いったん戻る
    setBroadcastRows(resequenced);
    // サーバ保存 (全件 onair_order を送信)
    (async () => {
      try {
        // 全件更新 API 呼び出し
        await Promise.all(
          resequenced.map(r => {
            const body = new FormData();
            body.append('props', JSON.stringify({ id: r.id, onair_order: r.onair_order, change_account: props.user.username }));
            return authFetch(`${apiURL}/broadcasts/update`, { method: 'POST', body, actionName: 'ChangeOnAirOrder' });
          })
        );
        // 放送リスト更新
        setBroadcastRows(resequenced);
      } 
      catch (e) {
        // 失敗したらロールバック
        setBroadcastRows(backup);
        // 失敗トーストは表示
        showToast('OA順の保存/再付番に失敗しました', 'error');
      }
    })();
  }, [broadcastRows, setBroadcastRows, setInterviewRows, showToast]);

  // ドラック終了時の行並び変更処理
  const handleChangeRows = useCallback((list) => {
    // 放送リスト再採番共通処理 (force=false)
    resequenceBroadcast(list, { force: false });
  }, [resequenceBroadcast]);

  // 放送予定リスト更新
  const broadcastRowUpdate = async (row, order) => {
    console.log('放送予定更新開始', row, order);
    // DB検索API呼び出し用
    let existBroadcast = null;
    // OA順が指定されていない場合
    if (order === -1) {
      // 最大onair_order取得
      const maxOrder = broadcastRows.reduce((max, r) => r.onair_order > max ? r.onair_order : max, 0);
      order = maxOrder + 1
    }
    // API呼び出し用
    const body = new FormData();
    const form_params = { };
    form_params.program_date = row.program_date;
    form_params.interview_id = row.id;
    form_params.clip_id = null;
    form_params.onair_order = order; // 指定された順番に設定
    form_params.entry_account = props.user.username;
    form_params.change_account = props.user.username;
    // 更新処理
    try {
      // 放送予定が存在するか確認のためAPI呼び出し
      const query_params = new URLSearchParams({ interview_id: row.id });
      const data = await authJson(`${apiURL}/broadcasts?` + query_params);
      console.log('放送予定検索結果', data);
      if (data && Array.isArray(data) && data.length > 0) {
        existBroadcast = data[0];
      }
      // 放送予定が存在する場合
      if (existBroadcast) {
        // 既存のidを追加する
        form_params.id = existBroadcast.id;
        // 放送予定　更新API呼び出し
        body.append('props', JSON.stringify(form_params));
        await authFetch(`${apiURL}/broadcasts/update`, { method: 'POST', body, actionName: 'AddInterviewToBroadcast' });
      }
      // 放送予定が存在しない場合
      else {
        // 放送予定　新規登録API呼び出し
        body.append('props', JSON.stringify(form_params));
        const res = await authFetch(`${apiURL}/broadcasts/create`, { method: 'POST', body, actionName: 'AddInterviewToBroadcast' });
        const result = await res.json();
        console.log("DB結果", result)
        if (result?.id) {
          // 新規登録したidを追加する
          form_params.id = result?.id
        }
      }
      // 放送リストの指定位置へ新規追加した新配列作成
      const newItem = form_params;    // 追加した要素
      const insertIndex = order - 1;  // orderが1始まりの場合
      const newRows = [
        ...broadcastRows.slice(0, insertIndex),
        newItem,
        ...broadcastRows.slice(insertIndex)
      ];
      // 放送リスト再採番共通処理 (force=true)
      resequenceBroadcast(newRows, { force: true });

    } catch(e) {
      console.log(e);
      throw e;
    }
  };

  // 放送予定リスト行削除
  const broadcastRowDelete = async (row) => {
    console.log('放送予定削除開始', row);
    // API呼び出し用
    const body = new FormData();
    const form_params = { };
    form_params.id = row.id;
    form_params.program_date = null;
    form_params.onair_order = null;
    form_params.change_account = props.user.username;
    // 更新処理
    try {
      // 放送予定が取材の場合
      if (row.interview_id) {
        // 放送予定　更新API呼び出し（取材IDは消さないで放送日と順番をNULLにする）
        body.append('props', JSON.stringify(form_params));
        await authFetch(`${apiURL}/broadcasts/update`, { method: 'POST', body, actionName: 'RemoveInterviewToBroadcast' });
      }
      // 放送予定が固定クリップの場合
      else if (row.clip_id) {
        // 放送予定　削除API呼び出し
        body.append('props', JSON.stringify(form_params));
        await authFetch(`${apiURL}/broadcasts/delete`, { method: 'POST', body, actionName: 'RemoveClipToBroadcast' });
      }
    } catch(e) { 
      console.log(e); 
      throw e;
    }
  };

  // 未放送リストから放送予定リストへ追加
  async function addInterviewToBroadcast(newRow, newOrder) {
    // DB更新
    const body = new FormData();
    body.append('props', JSON.stringify({ 
      id: newRow.id, 
      program_date: newRow.program_date, 
      change_account: props.user.username }));
    try {
      // API呼び出し
      await authFetch(`${apiURL}/interviews/update`, { method: 'POST', body, actionName: 'EntryProgramDate' });
      // 放送予定リスト更新
      await broadcastRowUpdate(newRow, newOrder);
      // 取材リスト再読み込み（放送予定の有無で表示が変わるため）
      fetchInterviewRows();
    } catch(e) {
      // 失敗トースト表示
      showToast('放送予定の追加に失敗しました', 'error');
    }
  }

  // 固定クリップリストから放送予定リストへ追加
  async function addClipToBroadcast(row, order) {
    console.log('放送予定更新開始', row, order);
    // OA順が指定されていない場合
    if (order === -1) {
      // 最大onair_order取得
      const maxOrder = broadcastRows.reduce((max, r) => r.onair_order > max ? r.onair_order : max, 0);
      order = maxOrder + 1
    }
    // API呼び出し用
    const body = new FormData();
    const form_params = { };
    form_params.program_date = row.program_date;
    form_params.interview_id = null;
    form_params.clip_id = row.id;
    form_params.onair_order = order; // 指定された順番に設定
    form_params.entry_account = props.user.username;
    // 更新処理
    try {
        // 放送予定　新規登録API呼び出し
        body.append('props', JSON.stringify(form_params));
        const res = await authFetch(`${apiURL}/broadcasts/create`, { method: 'POST', body, actionName: 'AddClipToBroadcast' });
        const result = await res.json();
        console.log("DB結果", result)
        if (result?.id) {
          // 新規登録したidを追加する
          form_params.id = result?.id
        }
        // 放送リストの指定位置へ新規追加した新配列作成
        const newItem = form_params;    // 追加した要素
        const insertIndex = order - 1;  // orderが1始まりの場合
        const newRows = [
          ...broadcastRows.slice(0, insertIndex),
          newItem,
          ...broadcastRows.slice(insertIndex)
        ];
        // 放送リスト再採番共通処理 (force=true)
        await resequenceBroadcast(newRows, { force: true });
        // 放送予定リスト再読み込み
        fetchBroadcastRows();

    } catch(e) {
      // エラー
      console.log("放送予定更新エラー", e)
      // 失敗トースト表示
      showToast('放送予定の追加に失敗しました', 'error');
    }
  };

  // 放送予定リスト削除処理
  const handleDeleteRow = async (row) => {
   try {
      // 放送予定が取材の場合
      if (row.interview_id) {
        // 取材リストDB更新
        const body = new FormData();
        body.append('props', JSON.stringify({ id: row.interview_id, program_date: null, change_account: props.user.username }));
        // API呼び出し
        await authFetch(`${apiURL}/interviews/update`, { method: 'POST', body, actionName: 'ClearProgramDate' });
        // 放送予定リスト行削除
        await broadcastRowDelete(row);
        // 取材リスト再読み込み（放送予定の有無で表示が変わるため）
        fetchInterviewRows();
      }
      // 放送予定が固定クリップの場合
      else if (row.clip_id) {
        // 放送予定リスト行削除
        await broadcastRowDelete(row);
        // 放送予定リスト再読み込み
        fetchBroadcastRows();
      }
    }
    catch(e) {
      // 失敗トースト表示
      showToast('放送予定の削除に失敗しました', 'error');
    }
  }

  // ドラッグ開始時
  const handleDragStart = (event) => {
    setActiveId(event.active.id);
    // 重要：開始した瞬間に body を「ドラッグモード」にする
    document.body.classList.add('is-dragging');
    document.body.style.setProperty('cursor', 'copy', 'important');
  };

  // ドラッグ移動中 (重要！)
  const handleDragOver = (event) => {
    const { over } = event;
    // over が null でなければ（＝左側の Droppable エリアにマウスがいれば）
    // CSS 側の #scrollable-container ルールが自動的に適用されて「＋」になります。
    // ここでは状態更新だけ行い、カーソルの直接操作は不要です。
    if (over) {
      setCursorMode('copy');
    } else {
      setCursorMode('no-drop');
    }
  };

  // ドラッグキャンセル
  const handleDragCancel = () => {
    setActiveId(null); // IDをリセット
    document.body.classList.remove('is-dragging'); // クラス削除
    document.body.style.removeProperty('cursor'); // スタイル削除
  };

  // ドラッグ終了時の処理
  const handleDragEnd = useCallback(
    async (event) => {
      const { active, over } = event;

      setActiveId(null); // IDをリセット
      document.body.classList.remove('is-dragging'); // クラス削除
      document.body.style.removeProperty('cursor'); // スタイル削除

      // 1. ガード：ドロップ先がない、または自分自身なら終了
      if (!over || active.id === over.id) return;

      // 2. 右側から左側へのドロップ判定
      if (active.id.toString().startsWith('external-')) {
        const { type, row } = active.data.current;
    
        console.log("external drop", type, row)

        // コンテナへのドロップか、既存行へのドロップかを判定
        let targetOrder;
        const isContainer = over.id === 'draggable-datagrid-droppable';
        if (isContainer) {
          console.log("drop isContainer")
          // 空の時、またはコンテナIDの上に落としたら「最後」を指定
          targetOrder = -1;
        }
        else {
          const { index, isBottomHalf } = over.data.current;
          console.log("index drop", index, isBottomHalf)

          // 既存の行がある場合は、その行のインデックスを取得
          //const overIndex = broadcastRows.findIndex((r) => r.id === over.id);
          if (index == -1) return; // どこにも該当しない場合は終了
          // 挿入先OA順
          if (isBottomHalf)   // 下に挿入
            targetOrder = index + 2;
          else                // 上に挿入
            targetOrder = index + 1;
        }

        // 固定クリップリストからドロップ
        if (type === "clip") {
          // 表示中の日付を放送日に書き換えて新しい行を作成
          const selectDate = date.toLocaleDateString('ja-JP');
          const newRow = {
            ...row,
            program_date: selectDate,
          };
          // 固定クリップリストから放送予定リストへ追加
          addClipToBroadcast(newRow, targetOrder);
        }

        // 未放送リストからからドロップ
        if (type === "interview") {
          // 表示中の日付を放送日に書き換えて新しい行を作成
          const selectDate = date.toLocaleDateString('ja-JP');
          const newRow = {
            ...row,
            program_date: selectDate,
          };
          // 未放送リストから放送予定リストへ追加
          addInterviewToBroadcast(newRow, targetOrder);
        }

        return;
      }

      // --- 左側リスト内での並び替え ---
      const oldIndex = broadcastRows.findIndex((r) => r.id === active.id);
      const newIndex = broadcastRows.findIndex((r) => r.id === over.id);
      if (oldIndex !== -1 && newIndex !== -1) {
        const newList = arrayMove(broadcastRows, oldIndex, newIndex);
        handleChangeRows(newList);
      }
    },
    [broadcastRows, date, addInterviewToBroadcast, handleChangeRows]
  );

  // 放送トグル: 放送予定DB更新
  const toggleOnair = (rowId) => {
    // 対象行取得
    const target = interviewRows.find(r => r.id === rowId);
    if (!target) return;
    const nextValue = !target.is_onair;
    // API 呼び出し
    const body = new FormData();
    body.append('props', JSON.stringify({ id: rowId, is_onair: nextValue, change_account: props.user.username }));
    authFetch(`${apiURL}/interviews/update`, { method: 'POST', body, actionName: 'ChangeOnAir' })
      .then(() => {
        // 取材リスト再読み込み（放送予定の有無で表示が変わるため）
        fetchInterviewRows();
      })
      .catch(() => {
        // 失敗トーストは表示
        showToast('放送有無の保存に失敗しました', 'error');
      });
  };

  // 担当者トグル: 取材リストへ担当者リスト反映
  const togglePersonId = useCallback((rowId, uid) => {
    // 取材リスト更新
    setInterviewRows(prev => prev.map(r => {
      if(r.id !== rowId) return r;
      const list = Array.isArray(r.ucv_person_ids) ? [...r.ucv_person_ids] : [];
      const i = list.indexOf(uid);
      if(i >= 0) list.splice(i,1); else list.push(uid);
      return { ...r, ucv_person_ids: list };
    }));
  }, [setInterviewRows]);

  // 取材リスト更新 (担当者変更コミット)
  // 変更があればDB更新し、成功したらrows更新 & 編集モード停止
  // 失敗したら旧行を返す
  const interviewRowUpdate = async (newRow, oldRow) => {
    // 変更があればDB更新
    const oldIds = Array.isArray(oldRow?.ucv_person_ids) ? [...oldRow.ucv_person_ids].sort((a,b)=>a-b) : [];
    const newIds = Array.isArray(newRow?.ucv_person_ids) ? [...newRow.ucv_person_ids].sort((a,b)=>a-b) : [];
    const changed = JSON.stringify(oldIds) !== JSON.stringify(newIds);
    if(!changed) return newRow; // 変更なし
    // DB更新
    const body = new FormData();
    body.append('props', JSON.stringify({ id: newRow.id, ucv_person_ids: newRow.ucv_person_ids, change_account: props.user.username }));
    try {
      // API呼び出し
      await authFetch(`${apiURL}/interviews/update`, { method: 'POST', body, actionName: 'ChangeUcvPerson' });
      // 取材リスト更新 (順序維持)
      setInterviewRows(prev => prev.map(r => r.id === newRow.id ? { ...r, ...newRow } : r));
      // 成功トーストは控えめに（必要ならコメント解除）
      //showToast('担当者が保存されました', 'success');
      // 新しい行を返す
      return newRow;
    } catch(e) {
      // 失敗トースト表示
      showToast('担当者の保存に失敗しました', 'error');
      // 旧行を返す
      return oldRow || newRow;
    }
  }

  // 担当者Popover 閉鎖時コミット
  const commitPersonMenuChanges = useCallback(()=>{
    if(personMenuOpenRowId && personMenuOriginalRow){
      const current = interviewRows.find(r => r.id === personMenuOpenRowId);
      if(current){
        // 変更があればDB更新
        interviewRowUpdate(current, personMenuOriginalRow);
      }
    }
    setPersonMenuOriginalRow(null);
  }, [personMenuOpenRowId, personMenuOriginalRow, interviewRows]);

  // 担当者Popover 初期フォーカス
  useEffect(()=>{
    if(personMenuOpenRowId && personMenuPosition){
      setTimeout(()=>{
        const first = document.querySelector('#persons-popover [data-person-menu-item]');
        if(first) first.focus();
      },30);
    }
  },[personMenuOpenRowId, personMenuPosition]);

  // 担当者Popover キーハンドリング
  useEffect(()=>{
    const onKey = (e)=>{
      if(!personMenuOpenRowId) return;
      if(e.key === 'Escape' || e.key === 'Enter') {
        // Esc/Enter: 閉じる (変更があればコミット)
        commitPersonMenuChanges();
        setPersonMenuPosition(null);
        setTimeout(()=> setPersonMenuOpenRowId(null), 5);
      }
    };
    window.addEventListener('keydown', onKey, true);
    return ()=> window.removeEventListener('keydown', onKey, true);
  },[personMenuOpenRowId, commitPersonMenuChanges]);

  // 取材グリッドセルクリック処理抽出
  const handleInterviewCellClick = useCallback((params, event) => {
    // 担当者列クリックの場合
    if (params.field === 'ucv_person_ids') {
      // 担当者の▼アイコンクリックかどうか判定
      const isIcon = event.target && event.target.closest && event.target.closest('[data-role="open-person-select"]');
      if (isIcon) {
        // ▼アイコンクリック: 担当者 Popover 開く
        event.stopPropagation();
        if (apiRef.current) {
          const field = 'ucv_person_ids';
            const mode = apiRef.current.getCellMode(params.id, field);
            if (mode === 'view') apiRef.current.startCellEditMode({ id: params.id, field });
        }
        // 詳細画面へ遷移はしない
        return;
      }
    }
  }, [routerLocation, apiRef]);

  // レンダリング
  return (
    <Container sx={{ cursor: activeId ? cursorMode : 'default' }}>
      <Snackbar
        open={toastOpen}
        autoHideDuration={2500}
        onClose={()=> setToastOpen(false)}
        anchorOrigin={{ vertical:'bottom', horizontal:'left' }}
        sx={{ mb: 2, ml: 2 }}
      >
        <MuiAlert elevation={6} variant="filled" severity={toastSeverity} onClose={()=> setToastOpen(false)}>
          {toastMsg}
        </MuiAlert>
      </Snackbar>
      {/* 上部 */}
      <Box sx={{ width: '100%', mt: 2, mb: 0, px: 2, display: 'flex', flexDirection: 'row' }}>
        {/* 左側: 2段レイアウト (1段目: 日付 + 曜日, 2段目: ナビゲーションボタン) */}
        <Box sx={{ width: '50%', minWidth: 400, height: 'auto', px: 2, py: 0, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap:1 }}>
          {/* 1段目: 日付 */}
          <Box display="flex" alignItems="center" justifyContent="center">
            <Typography variant="inherit" sx={{ fontWeight: 'bold', fontSize: '1.6rem' }}>
              {date ? (()=>{ const w=['日','月','火','水','木','金','土'][date.getDay()]; return `${date.toLocaleDateString('ja-JP')} (${w})`; })() : ''}
            </Typography>
          </Box>
          {/* 2段目: ナビボタンを中央配置 */}
          <Stack direction="row" spacing={1} justifyContent="center" alignItems="center" sx={{ width:'100%' }}>
            <Box sx={{ width: '36px' }}></Box>
            <Button
              variant="text"
              sx={{ minWidth: 64, color: '#000' }}
              onClick={() => setDate(new Date(date.getFullYear(), date.getMonth(), date.getDate() - 1))}
              startIcon={<ArrowBackIosIcon sx={{ color: '#000' }} />}
            >前日</Button>
            <Button
              variant="text"
              color="primary"
              sx={{ minWidth: 64 }}
              onClick={() => setDate(new Date())}
            >本日</Button>
            <Button
              variant="text"
              sx={{ minWidth: 64, color: '#000' }}
              onClick={() => setDate(new Date(date.getFullYear(), date.getMonth(), date.getDate() + 1))}
              endIcon={<ArrowForwardIosIcon sx={{ color: '#000' }} />}
            >翌日</Button>
            <IconButton
              color="primary"
              aria-label="カレンダー"
              onClick={(e) => {
                if (calendarOpen) {
                  setCalendarOpen(false);
                  setCalendarAnchor(null);
                } else {
                  setCalendarAnchor(e.currentTarget);
                  setCalendarOpen(true);
                }
              }}
            >
              <EventIcon />
            </IconButton>
          </Stack>
          {/* 3段目: （タブは下部リストエリアへ移動） */}
        </Box>
        {/* 右側: なにもなし */}
        <Box sx={{ width: '50%', px: 2, py: 0, display:'flex', flexDirection:'column', justifyContent:'flex-end', alignItems:'flex-end' }}>
        </Box>
      </Box>
      {/* 下部: 左リスト + 左リスト上下２つ */}
      <Box display="flex" flexDirection="row" sx={{ width: '100%', alignItems: 'flex-start', px: 2 }}>
        <DndContext
          sensors={sensors}
          collisionDetection={pointerWithin}
          onDragStart={handleDragStart}
          onDragOver={handleDragOver}
          onDragCancel={handleDragCancel}
          onDragEnd={handleDragEnd}
          modifiers={[
            restrictToVerticalAxis,
            restrictToWindowEdges,
            restrictToParentElement
          ]}
          //autoScroll={true}
          autoScroll={{
            threshold: { x: 0, y: 0.1 }, // 端っこに来た時の感度
            canScroll: (element) => {
              // id が "scrollable-container" の要素とその中身以外はスクロールさせない
              return element.closest('#scrollable-container') !== null;
            }
          }}
        >      
          {/* 左側: 放送予定 */}
          <Box sx={{ width: '50%', minWidth: 400, height: '620px', px: 2, py: 0, display:'flex', flexDirection:'column' }}>
            <Tabs
              value="broadcast"
              textColor="primary"
              indicatorColor="primary"
              sx={{
                minHeight:34,
                mb:0,
                px:0,
                '& .MuiTabs-flexContainer': { mb:0 },
                '& .MuiTab-root': { minHeight:34, py:0, m:0, minWidth:100 },
                borderBottom:'1px solid #ddd'
              }}
            >
              <Tab
                label="放送予定"
                value="broadcast"
                disableRipple
                sx={{
                  backgroundColor: '#f5f5f5',
                  '&.Mui-selected': { fontWeight: 'bold' }
                }}
              />
            </Tabs>
              <LeftDroppableContainer>
                <DraggableDataGrid
                  columns={broadcastColumns}
                  rows={broadcastRows}
                  activeId={activeId}
                />
                {/* フッター: 放送尺合計 */}
                <Box sx={{ width:'100%', background:'#f5f5f5', border:'1px solid #ddd', p:1, textAlign:'right', /*fontWeight:'bold', fontSize:'1.1rem'*/ }}>
                  総尺：{sumProgramLength(broadcastRows)}
                </Box>
              </LeftDroppableContainer>
          </Box>
          {/* 右側: 未放送データと固定クリップ（上下2分割） */}
          <Box sx={{ width: '50%', height: '620px', px: 2, py: 0, display: 'flex', flexDirection: 'column' }}>
            {/* 上側: 未放送データ */}
            <Box sx={{ height: '50%' }}>
              <Tabs
                value="interview"
                textColor="primary"
                indicatorColor="primary"
                sx={{
                  minHeight:34,
                  mb:0,
                  px:0,
                  '& .MuiTabs-flexContainer': { mb:0 },
                  '& .MuiTab-root': { minHeight:34, py:0, m:0, minWidth:100 },
                  borderBottom:'1px solid #ddd'
                }}
              >
                <Tab
                  label="未放送データ"
                  value="interview"
                  disableRipple
                  sx={{
                    backgroundColor: theme.palette.secondary.light,
                    borderRight: '1px solid #ddd',
                    '&.Mui-selected': { fontWeight: 'bold' }
                  }}
                />
              </Tabs>
              <Box sx={{ flex:1, mt:0, display:'flex', minHeight:0, position:'relative', height: 'calc(100% - 34px)' }}>
                <ThemeDataGrid
                  rows={interviewRows}
                  columns={interviewColumns}
                  localeText={jaJP.components.MuiDataGrid.defaultProps.localeText}
                  columnVisibilityModel={{ id: false }}
                  disableColumnMenu
                  disableColumnSorting
                  hideFooter
                  sx={{ cursor: 'pointer', borderTop:'none', height:'100%' }}
                  onCellClick={(params, event) => {
                    handleInterviewCellClick(params, event);
                  }}
                  editMode="cell"
                  apiRef={apiRef}
                />
              </Box>
            </Box>
            {/* 下側: 固定クリップ */}
            <Box sx={{ height: '50%', mt: 2 }}>
              <Tabs
                value="clip"
                textColor="primary"
                indicatorColor="primary"
                sx={{
                  minHeight:34,
                  mb:0,
                  px:0,
                  '& .MuiTabs-flexContainer': { mb:0 },
                  '& .MuiTab-root': { minHeight:34, py:0, m:0, minWidth:100 },
                  borderBottom:'1px solid #ddd'
                }}
              >
                <Tab
                  label="固定クリップ"
                  value="clip"
                  disableRipple
                  sx={{
                    backgroundColor: theme.palette.secondary.light,
                    borderRight: '1px solid #ddd',
                    '&.Mui-selected': { fontWeight: 'bold' }
                  }}
                />
              </Tabs>
              <Box sx={{ flex:1, mt:0, display:'flex', minHeight:0, position:'relative', height: 'calc(100% - 34px)' }}>
                <ThemeDataGrid
                  rows={clipRows}
                  columns={clipColumns}
                  localeText={jaJP.components.MuiDataGrid.defaultProps.localeText}
                  columnVisibilityModel={{ id: false }}
                  disableColumnMenu
                  disableColumnSorting
                  hideFooter
                  sx={{ cursor: 'pointer', borderTop:'none', height:'100%' }}
                  editMode="cell"
                  apiRef={apiRef}
                />
              </Box>
            </Box>
          </Box>
          {/* 担当者グローバルPopover */}
          <Popover
            id="persons-popover"
            open={!!personMenuOpenRowId && !!personMenuPosition}
            anchorReference="anchorPosition"
            anchorPosition={personMenuPosition || { top:0, left:0 }}
            onClose={()=>{ commitPersonMenuChanges(); setPersonMenuPosition(null); setTimeout(()=> setPersonMenuOpenRowId(null), 30); }}
            transitionDuration={{ appear:150, enter:150, exit:80 }}
            slotProps={{ paper: { sx:{ p:0, minWidth:180 } } }}
          >
            {personMenuOpenRowId && (
              <Box sx={{ width:'100%', maxHeight:230, overflowY:'auto', py:0 }} role="listbox"
                onKeyDown={(e)=>{
                  if(e.key === 'Enter') { e.preventDefault(); e.stopPropagation(); commitPersonMenuChanges(); setPersonMenuPosition(null); setTimeout(()=> setPersonMenuOpenRowId(null), 5); return; }
                  if(e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); commitPersonMenuChanges(); setPersonMenuPosition(null); setTimeout(()=> setPersonMenuOpenRowId(null), 5); return; }
                  if(e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                    e.preventDefault(); e.stopPropagation();
                    const items = personMenuItemRefs.current.filter(Boolean);
                    if(!items.length) return;
                    const idx = items.indexOf(document.activeElement);
                    let next = 0;
                    if(e.key === 'ArrowDown') next = idx < items.length -1 ? idx+1 : 0; else next = idx > 0 ? idx-1 : items.length-1;
                    items[next].focus();
                    return;
                  }
                }}
              >
                {(() => {
                  const row = interviewRows.find(r => r.id === personMenuOpenRowId);
                  const ids = Array.isArray(row?.ucv_person_ids) ? row.ucv_person_ids : [];
                  personMenuItemRefs.current = [];
                  return users.map(u => {
                    const checked = ids.indexOf(u.id) > -1;
                    return (
                      <MenuItem
                        key={u.id}
                        data-person-menu-item
                        value={u.id}
                        data-value={u.id}
                        selected={checked}
                        tabIndex={0}
                        ref={el => { if(el) personMenuItemRefs.current.push(el); }}
                        onClick={(e)=>{ 
                          // クリック元がチェックボックスなら親で重複トグルしない
                          if(skipNextClickRef.current){
                            skipNextClickRef.current = false; // 一度だけ無効化
                            return;
                          }
                          if(e.target && (e.target.closest && e.target.closest('[data-checkbox]'))) { return; }
                          e.stopPropagation(); togglePersonId(row.id, u.id); 
                        }}
                        onKeyDown={(e)=>{
                          if(e.code === 'Space' || e.key === ' '){
                            e.preventDefault(); e.stopPropagation();
                            skipNextClickRef.current = true; // 後続click抑止
                            togglePersonId(row.id, u.id);
                          }
                        }}
                        sx={{ display:'flex', alignItems:'center', gap:1, py:0.5 }}
                      >
                        <Checkbox
                          size="small"
                          checked={checked}
                          data-checkbox
                          onClick={(e)=>{ e.stopPropagation(); togglePersonId(row.id, u.id); }}
                          onChange={(e)=>{ /* swallow */ }}
                          tabIndex={-1}
                        />
                        <ListItemText
                          primary={u.name}
                          sx={{ flex:1, minWidth:0, overflow:'hidden' }}
                          primaryTypographyProps={{ noWrap: true }}
                        />
                      </MenuItem>
                    );
                  });
                })()}
              </Box>
            )}
          </Popover>
          {/* カレンダー表示 (Popover) */}
          <Popover
            open={calendarOpen}
            anchorEl={calendarAnchor}
            onClose={() => { setCalendarOpen(false); setCalendarAnchor(null); }}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            transformOrigin={{ vertical: 'top', horizontal: 'center' }}
            slotProps={{ paper: { sx: { p: 1 } } }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mb: 1 }}>
              <Button size="small" onClick={() => { setCalendarOpen(false); setCalendarAnchor(null); }}>閉じる</Button>
            </Box>
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={jaLocale}>
              <StaticDatePicker
                displayStaticWrapperAs="desktop"
                value={date}
                onChange={newValue => {
                  if (newValue) setDate(newValue);
                }}
              />
            </LocalizationProvider>
          </Popover>
        </DndContext>
      </Box>
    </Container>
  );
};

export default BroadcastEditPage;